import {
  getOIEStateHandle,
  getOktaUserId,
  getOktaWidgetModel,
  showFormErrors,
  startSpinnerAndHideForm,
  stopSpinner,
  stopSpinnerAndShowForm
} from '../../../utils/widget-facade';
import { getAnetUsername, isAnetUser, isProdUserInSupport } from '../../../utils/user-utils';
import AmplitudeUtils from '../../../utils/amplitude-utils';
import { ERROR_KEYS, ERROR_MESSAGES } from '../../../constants';
import { setSecurityQuestionsPromptCookies } from '../../../utils/storage-utils';
import PasswordResetOieBaseCustomizer from './base';
import PasswordRequirements from '../../../components/password-reset/PasswordRequirements';
import { passwordResetOie, shouldPromptForSymantecVIPOIE } from '../../../utils/anet-client';
import { getLocalizedString } from '../../../utils/i18n-utils';
import athenaConfig from '../../../config';

interface SymantecPromptPayloadOie {
  username: string;
  stateHandle: string;
}
export default class PasswordResetOieAnetCustomizer extends PasswordResetOieBaseCustomizer<SymantecPromptPayloadOie> {
  constructor(args: any) {
    super(args);
    this.symantecFieldIndex = 0;
  }
  protected onPasswordReset() {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this;
    const {
      oktaSignIn: { authClient },
    } = this;
    const originalProceed = authClient.idx.proceed.bind(authClient);

    // Widget calls this method after performing frontend validation with the expected payload
    // See https://github.com/okta/okta-signin-widget/blob/7.8/src/v2/controllers/FormController.ts#L284
    authClient.idx.proceed = async (...args: any) => {
      const model = getOktaWidgetModel();
      //Handle errors by listing to error events once the button is clicked. This helps in receiving a more "normalized"
      //error payload - See https://github.com/okta/okta-signin-widget/blob/7.8/src/v2/controllers/FormController.ts#L385
      model.once('error', (_: any, error: any) => {
        if (this.isSymantecValidationError(error.responseJSON?.message)) {
          this.clearPasswordResetForm();
        }
        else {
          this.clearSymantecCode();
        }
        const errorMessage =
          error?.responseJSON?.errorCauses?.[0]?.errorSummary ??
          error?.responseJSON?.xhr?.responseJSON?.errorSummary ??
          error?.errorSummary ??
          ERROR_MESSAGES[ERROR_KEYS.GENERIC_INTERNAL_ERROR];
        AmplitudeUtils.logPasswordResetEvent('PasswordChangeFailed', {
          errorMessage,
          shouldPromptForSymantec: self.shouldPromptForSymantec,
        });
      });

      AmplitudeUtils.logPasswordResetEvent('PasswordChangeInitiated', {
        shouldPromptForSymantec: self.shouldPromptForSymantec,
      });
      const { step } = args[0];
      if ([ 'reenroll-authenticator-warning', 'reenroll-authenticator' ].includes(step)) {
        const {
          credentials: { passcode },
          stateHandle,
        } = args[0];
        const symantecSecurityCode = this.getSymantecSecurityCode();
        const response = await self.triggerPasswordResetRequest({
          symantecSecurityCode,
          newPassword: passcode,
          stateHandle,
        });
        if (response.sqtoken) {
          setSecurityQuestionsPromptCookies({
            sqtoken: response.sqtoken,
            primaryAuthWithCustomTxn: true,
          });
        }

        const requiresMfa = response.remediation?.value?.some(
          (r: any) => r.name === 'select-authenticator-authenticate' || r.name === 'select-authenticator-enroll'
        );
        const requestSucceeded = athenaConfig.featureFlags.enableExpiredPasswordResetViaLambda ?
          !!response.success : !response.error;

        if (requestSucceeded || requiresMfa) {
          AmplitudeUtils.logPasswordResetEvent('PasswordChangeSuccess', {
            shouldPromptForSymantec: self.shouldPromptForSymantec,
          });
        }
        const passwordUpdateSucceeded = requestSucceeded || requiresMfa;

        const idxResponse = await authClient.idx.makeIdxResponse(response, {}, passwordUpdateSucceeded);
        await authClient.transactionManager.saveIdxResponse({
          rawIdxResponse: idxResponse,
          requestDidSucceed: passwordUpdateSucceeded,
          stateHandle,
        });
        return idxResponse;
      }
      return originalProceed.apply(authClient, args);
    };
  }

  protected customizePasswordResetButton() {
    // Monkey patch model's validate method and add on the Symantec code's validation.
    // This lets us keep using Okta's validations out of the box for rest of the fields
    // For classic - https://github.com/okta/okta-signin-widget/blob/7.8/src/v1/controllers/PasswordExpiredController.js#L31
    // For OIE - https://github.com/okta/okta-signin-widget/blob/7.8/src/v2/view-builder/views/password/EnrollAuthenticatorPasswordView.js#L86
    const model = getOktaWidgetModel();
    const originalValidate = model.validate.bind(model);
    model.validate = () => {
      const response = originalValidate();
      const symantecCodeValid = this.isSecurityCodeValid();
      if (response) {
        return response;
      }
      else {
        // return a hash when symantec token is not valid so widget set the top error banner
        return symantecCodeValid ? undefined : { 'symantec-token': 'error' };
      }
    };
    this.onPasswordReset();
  }

  protected forgifyPasswordRequirements() {
    const { headerSelector, itemListSelector, containerSelector } = this.getPasswordRequirementsSelector();
    const passwordRequirements: JQuery = jQueryCourage(containerSelector);
    const component = (
      <PasswordRequirements
        passwordRequirements={passwordRequirements}
        headerSelector={headerSelector}
        itemListSelector={itemListSelector}
      />
    );

    this.renderForgeAndHideOktaComponent({
      id: passwordRequirements.attr('data-se')!,
      forgeComponent: component,
      oktaComponent: passwordRequirements,
    });
  }

  async shouldCustomize() {
    return (await super.shouldCustomize()) && isAnetUser();
  }

  async customize() {
    try {
      startSpinnerAndHideForm();
      const prodUserInSupport = isProdUserInSupport();
      if (prodUserInSupport && (this.context.formName === 'reenroll-authenticator-warning')) {
        this.skipPasswordResetWarningForProdUser();
        return;
      }
      await super.customize();
      if (prodUserInSupport) {
        this.blockPasswordUpdateForProdUser();
        return;
      }
      await this.handleSymantecPrompt();
      stopSpinnerAndShowForm();
    }
    catch (error: any) {
      await this.handleSymantecPromptError(error);
    }
  }

  async getSymantecPromptPayload(): Promise<SymantecPromptPayloadOie> {
    return {
      username: getAnetUsername(),
      stateHandle: getOIEStateHandle(),
    };
  }

  async handleSymantecPromptError(error: any): Promise<void> {
    const errorMessage = error.messages?.value?.[0]?.message ?? error.errorSummary ?? error.message;
    AmplitudeUtils.logPasswordResetEvent('SymantecVipPromptFailure', {
      errorMessage,
    });
    error.errorSummary = errorMessage === 'Failed to fetch' ? getLocalizedString('error.default.description') : errorMessage;
    showFormErrors(error);
    stopSpinner();
  }

  async triggerPasswordResetRequest({ newPassword, symantecSecurityCode }: any): Promise<any> {
    const symantecPromptPayload = await this.getSymantecPromptPayload();
    return passwordResetOie({
      newPassword,
      symantecSecurityCode,
      ...symantecPromptPayload,
      clientId: this.settings.clientId,
    });
  }

  protected async shouldPromptForSymantecVIP(payload: any): Promise<boolean> {
    return shouldPromptForSymantecVIPOIE(payload);
  }

  protected async handleSymantecPrompt() {
    const checkPayload = await this.getSymantecPromptPayload();
    this.shouldPromptForSymantec = await this.shouldPromptForSymantecVIP(checkPayload);
    if (this.shouldPromptForSymantec) {
      this.customizeForEpcs();
      AmplitudeUtils.logPasswordResetEvent('SymantecVipPrompted', {
        userId: getOktaUserId(),
      });
    }
  }
  protected getSymantecCodeTarget(): JQuery {
    return jQueryCourage('#athena-password-authenticator--rules');
  }

  protected skipPasswordResetWarningForProdUser() {
    jQueryCourage('[data-se="skip"]').trigger('click');
    stopSpinner();
  }

}
